import _ from 'lodash';
import {gzip, ungzip} from 'pako';
import {Observable} from 'rxjs';
import 'rxjs/add/operator/first';
import 'rxjs/add/operator/toPromise';
import EventEmitter from 'wolfy87-eventemitter';
import observableService from './observableService';

/**
 * Minimum serialized-JSON size (in characters ≈ bytes for ASCII) at which a
 * value is worth gzip-compressing before being written to localStorage.
 *
 * Below this threshold the CPU cost of gzip+base64 (both on write and on every
 * read) outweighs the space savings — even hundreds of sub-threshold entries
 * are rounding error against the 5 MB localStorage quota. Above it, typical
 * compression ratios of 7–20% turn cache entries that would otherwise exhaust
 * the quota (translation bundles, account trees) into something that fits
 * comfortably.
 */
const COMPRESSION_THRESHOLD_BYTES = 10 * 1024;

/**
 * One-character prefixes on the stored string so reads can cheaply tell a
 * compressed value from a raw one without parsing an envelope. Pre-threshold
 * entries written by an older version of this file will have neither prefix;
 * they fail to decode and are evicted + refetched on next read.
 */
const COMPRESSED_PREFIX = 'g:';
const RAW_PREFIX = 'r:';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Serializes a cache value for localStorage. Small values are stored as raw
 * JSON (skips compression overhead); large values are gzip+base64-encoded.
 */
export function encodeForStorage(value: unknown): string {
  const json = JSON.stringify(value);
  if (json.length < COMPRESSION_THRESHOLD_BYTES) {
    return RAW_PREFIX + json;
  }
  return COMPRESSED_PREFIX + bytesToBase64(gzip(json));
}

/**
 * Inverse of `encodeForStorage`. Throws if the stored value is in an unknown
 * format (e.g. written by an older build), which the caller treats as a cache
 * miss and evicts.
 */
export function decodeFromStorage(stored: string): unknown {
  // Builds before this encoding existed put the response OBJECT into the cache
  // rather than a serialized string, so a tab upgraded across that change reads
  // a non-string here. It is an unknown format like any other; without this
  // guard it surfaces as `stored.startsWith is not a function`, which reads as
  // a code bug rather than as the stale entry it is.
  if (typeof stored !== 'string') {
    throw new Error('Unknown localCache storage format');
  }
  if (stored.startsWith(RAW_PREFIX)) {
    return JSON.parse(stored.slice(RAW_PREFIX.length));
  }
  if (stored.startsWith(COMPRESSED_PREFIX)) {
    const json = ungzip(base64ToBytes(stored.slice(COMPRESSED_PREFIX.length)), {to: 'string'});
    return JSON.parse(json);
  }
  throw new Error('Unknown localCache storage format');
}

export type LocalCacheArgs<ProviderResponse, Output> = {
  cacheFactory: any,
  cacheName: string,
  cacheKey: string,
  cacheTime: number,
  provider: any,
  cacheStorage: 'memory' | 'localStorage',
  postProcessor: (response: ProviderResponse) => Output
}

export type Listener = (error: Error | undefined, value?: unknown) => unknown;
interface ListenerWrapper extends Function {
  once: boolean,
  listener: Listener
}

export const storagePrefix = 'nx__';

export default class LocalCache<ProviderResponse, Output> {
  private provider: any; // function calling http.get
  private emitter: EventEmitter;
  private currentlyFetching: boolean;
  private postProcessor: any;
  private cacheKey: string;
  private cache: any;
  private cacheName: string;
  private cacheTime: number | undefined;
  private cacheFactory: any;

  constructor({cacheFactory, cacheName, cacheTime, provider, postProcessor, cacheKey = 'key', cacheStorage = 'localStorage'}: Partial<LocalCacheArgs<ProviderResponse, Output>> & {cacheName: string}) {
    this.provider = provider;
    this.emitter = new EventEmitter();
    this.currentlyFetching = false;
    this.postProcessor = postProcessor;

    this.cacheKey = cacheKey;
    this.cacheName = cacheName;
    this.cacheTime = cacheTime;

    this.cache = cacheFactory.get(cacheName);
    this.cacheFactory = cacheFactory;
    if (!this.cache) {
      this.cache = this.createCache(cacheStorage);
    }
  }

  private createCache(cacheStorage: string): LocalCache<any, any> {
    return this.cacheFactory.createCache(this.cacheName, {
      deleteOnExpire: 'aggressive',
      storageMode: cacheStorage,
      storagePrefix: storagePrefix,
      ...this.cacheTime && {maxAge: this.cacheTime}
    });
  }

  private getCacheValue(): Output {
    const raw = this.cache.get(this.cacheKey);
    if (this.inMemoryCache()) {
      return _.cloneDeep(raw);
    }
    if (raw === undefined || raw === null) {
      return raw;
    }
    try {
      return decodeFromStorage(raw) as Output;
    } catch (e) {
      console.error('Error decoding cache value, removing it', this.cacheName, e);
      this.cache.remove(this.cacheKey);
      return undefined as unknown as Output;
    }
  }

  private putCacheValue(value: unknown): void {
    if (this.inMemoryCache()) {
      this.cache.put(this.cacheKey, value);
      return;
    }
    this.cache.put(this.cacheKey, encodeForStorage(value));
  }

  subscribe(listener: Listener): void {
    this.emitter.addListener(this.cacheKey, listener);
    const lastValue = this.getCacheValue();
    if (lastValue) {
      listener(undefined, lastValue);
    } else {
      this.refetch();
    }
  }

  inMemoryCache(): boolean {
    return this.cache.info().storageMode === 'memory';
  }

  async refetch(): Promise<Output | void> {
    if (this.fetching()) {
      console.log(`Already fetching ${this.cacheName} ${this.cacheKey}`);
      return Promise.resolve();
    }

    console.log(`Removing cache ${this.cacheName} ${this.cacheKey}`);
    this.cache.remove(this.cacheKey);

    const providerResponse = this.provider();

    if (providerResponse.errorCallback.length === 0) {
      // there is no a single code invocation that handles errors in subscribe, so
      // let's display http error notification
      providerResponse.error((error: unknown, status: unknown) => {
        providerResponse.defaultErrorCallback(error, status);
      });
    }

    this.fetching(true);
    return new Promise((resolve, reject) => {
      providerResponse.success((response: any) => {
        if (this.postProcessor) {
          response = this.postProcessor(response);
        }
        try {
          this.putCacheValue(response);
        } catch (e) {
          if (!this.inMemoryCache()) {
            console.error('Error setting cache, cache name', this.cacheName, e);
            this.cache.destroy(this.cacheName);
            this.cache = this.createCache('memory');
            this.putCacheValue(response);
            console.log('Replaced cache with in memory version, cache name:', this.cacheName);
          }
        }

        this.emit(undefined, this.getCacheValue());
        resolve(this.getCacheValue());
      })
        .error((error: unknown) => {
          // we need to trigger emission in case on an error
          // - otherwise promise will not be rejected if someone is waiting on subscribe or toPromise
          const err: any = new Error('Cache provider error');
          err.response = error;

          this.emit(err);
          reject(error);
        })
        .always(() => {
          this.fetching(false);
        });
    });
  }

  emit(error: undefined | Error, value?: unknown) : void {
    const listeners  = <ListenerWrapper[]> this.emitter.getListeners(this.cacheKey);
    // we need to copy listeners. The list is mutable and can change as a result of calling a listener
    const immutableListeners = Object.freeze([...listeners]);
    for (const listener of immutableListeners) {
      try {
        if (listener.once) {
          console.log('removing listener', this.cacheName);
          this.emitter.removeListener(this.cacheKey, listener.listener);
        }

        listener.listener(error, _.cloneDeep(value));
      } catch (err) {
        console.error(`Error emitting listener. Cache key: ${this.cacheKey} ${this.cacheName}`);
        listener.listener(err);
      }
    }
  }

  evict(): void {
    console.log('Evicting cache for', this.cacheName, '-', this.cacheKey);
    this.cache.remove(this.cacheKey);
  }

  fetching(value?: boolean): boolean {
    if (value !== undefined) {
      this.currentlyFetching = value;
    }

    return !!this.currentlyFetching;
  }

  unsubscribe(listener: Listener): void {
    this.emitter.removeListener(this.cacheKey, listener);
  }

  toObservable(): Observable<Output> {
    return observableService(this);
  }

  toPromise(): Promise<Output> {
    return observableService(this)
      .first()
      .toPromise();
  }
}